import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  TouchableOpacity,
  Image,
  Dimensions,
} from 'react-native';
import TopGreenHeader from '../Common/TopGreenHeader';
import { useNavigation } from '@react-navigation/native';
import AddKidsModal from '../../components/AddKidsModal';
import { useSelector } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import RenderHtml from 'react-native-render-html';
import { toast } from 'react-toastify';
import API_PATHS from '../../apiCalls/ApiPath';

// Internal CSS for react-toastify
const toastifyCss = `
.Toastify__toast-container {
  z-index: 999999;
  font-family: sans-serif;
}
.Toastify__toast {
  border-radius: 4px;
  padding: 16px;
  font-size: 16px;
  z-index: 999999;
}
.Toastify__progress-bar {
  background: #A6CE39;
}`;

const EventDetails = () => {
  const navigation = useNavigation();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedKids, setSelectedKids] = useState([]);
  const [userId, setUserId] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [event, setEvent] = useState({
    title: '',
    date: '',
    registrationStatus: 'Registration Opened',
    statusType: 'upcoming',
    details: '',
    eventDates: [],
    availableSeats: 0,
    totalSeats: 0,
    isRegistered: false
  });
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [selectedKidsMap, setSelectedKidsMap] = useState(new Map());
  const [registrationStatus, setRegistrationStatus] = useState('Not Paid');
  const [showPayButton, setShowPayButton] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const windowWidth = Dimensions.get('window').width;
  const [loading, setLoading] = useState(true);

  const formatDate = (epochTime) => {
    const date = new Date(epochTime);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTime = (time) => {
    return time.replace(/(\d+):(\d+)/, (match, hour, minute) => {
      const h = parseInt(hour);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${minute} ${ampm}`;
    });
  };

  const fetchEventDetails = useCallback(async (eventId) => {
    try {
      const response = await axios.get(`${API_PATHS.BASE_URL}${API_PATHS.EVENT_DETAILS}/${eventId}`);
      if (response.data.Success) {
        const eventData = response.data.Data;
        const formattedDates = eventData.eventDates.map(date => {
          const formattedDate = formatDate(date.date);
          const startTime = formatTime(date.startTime);
          const endTime = formatTime(date.endTime);
          return `${formattedDate} ${startTime}-${endTime}`;
        }).join(', ');

        const getRegistrationStatus = () => {
          if (eventData.status === 'completed') {
            return 'Registration Closed';
          }
          return eventData.isRegistered ? 'Registration Opened (Booked)' : 'Registration Opened';
        };

        setEvent({
          ...eventData,
          date: formattedDates,
          registrationStatus: getRegistrationStatus(),
          statusType: eventData.status,
          details: eventData.details,
          eventDates: eventData.eventDates,
          availableSeats: eventData.available_seat_num,
          totalSeats: eventData.total_seats_num,
          isRegistered: eventData.isRegistered
        });

        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  }, []);

  const checkAuth = useCallback(async (eventId = null) => {
    const token = await AsyncStorage.getItem('token');
    if (!token) {
      if (eventId) {
        await AsyncStorage.setItem('eventId', eventId);
        window.location.href = '/?eventId=' + eventId;
      } else {
        navigation.navigate('LoginSignup');
      }
    } else {
      const storedUserId = await AsyncStorage.getItem('userId');
      if (!storedUserId) {
        navigation.navigate('LoginSignup');
      } else {
        setUserId(storedUserId);
        if (Platform.OS === 'web') {
          const urlParams = new URLSearchParams(window.location.search);
          const currentUserId = urlParams.get('userId');
          if (!currentUserId) {
            const newUrl = `${window.location.pathname}?${urlParams.toString()}&userId=${storedUserId}`;
            window.history.replaceState({}, '', newUrl);
          }
        }
      }
    }
  }, [navigation]);

  const checkRegistrationDetails = useCallback(async (userId, eventId) => {
    try {
      const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.REGISTRATION_DETAILS}`, {
        userId,
        eventId: parseInt(eventId)
      });

      console.log(response.data);

      if (response.data.Success === 1) {
        setRegistrationDetails(response.data.Data);
        setShowPayButton(true);
        setRegistrationStatus('Not Paid');
        setEvent(prev => ({ ...prev, isRegistered: true }));
      } else {
        setRegistrationDetails(null);
        setShowPayButton(false);
        setRegistrationStatus('');
        setEvent(prev => ({ ...prev, isRegistered: false }));
      }
    } catch (error) {
      console.error('Error checking registration details:', error);
      setRegistrationDetails(null);
      setShowPayButton(false);
      setRegistrationStatus('');
      setEvent(prev => ({ ...prev, isRegistered: false }));
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const eventId = urlParams.get('eventId');
    const storedUserId = urlParams.get('userId');

    if (eventId) {
      setEventId(eventId);
      checkAuth(eventId);
      fetchEventDetails(eventId);
    } else {
      checkAuth();
    }

    if (storedUserId) {
      setUserId(storedUserId);
    }
  }, [checkAuth, fetchEventDetails]);

  useEffect(() => {
    if (userId && eventId) {
      checkRegistrationDetails(userId, eventId);
    }
  }, [userId, eventId, checkRegistrationDetails]);



  const toggleKidSelection = (kid) => {
    const isSelected = selectedKids.find(k => k.id === kid.id);
    if (isSelected) {
      setSelectedKids(selectedKids.filter(k => k.id !== kid.id));
    } else {
      setSelectedKids([...selectedKids, kid]);
    }
  };

  const handleModalSave = (selectedTicket, selectedKidsForTicket) => {
    setSelectedPlans(prev => [...prev, selectedTicket]);
    setSelectedKidsMap(prev => {
      const newMap = new Map(prev);
      newMap.set(selectedTicket.ticketId, selectedKidsForTicket);
      return newMap;
    });
    setModalVisible(false);
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const handleBook = async () => {
    try {
      const parentId = await AsyncStorage.getItem('userId');
      if (!parentId) {
        toast.error('Please login to book the event', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }

      if (selectedPlans.length === 0) {
        toast.error('Please select at least one ticket', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }

      const tickets_baught = selectedPlans.map(plan => {
        const kidsForPlan = selectedKidsMap.get(plan.ticketId) || [];
        return {
          ticketid: plan.ticketId,
          kidsId: kidsForPlan.map(kid => kid.kidId),
          no_of_kids: kidsForPlan.length
        };
      });

      const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.REGISTER_EVENT}`, {
        parentId,
        eventId: parseInt(eventId),
        tickets_baught
      });

      if (response.data.Success) {
        toast.success(response.data.Message, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        window.location.reload();
      } else {
        toast.error(response.data.Message || 'Booking failed', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error('Error booking event:', error);
      toast.error('Failed to book event. Please try again.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const renderSelectedSummary = () => {
    if (registrationDetails) {
      return (
        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>Kid(s)</Text>

          {registrationDetails.map((registration, index) => {
            const kidsForPlan = registration.kidsDetails.map(kid => ({
              id: kid.kidId,
              name: kid.kidName || 'null'
            }));

            return (
              <View key={registration.registrationDetails?.registrationId}>
                <View style={styles.planSection}>
                  <Text style={styles.summaryText}>
                    {kidsForPlan.map(kid => kid.name).join(', ')}
                  </Text>

                  <Text style={styles.summaryPlan}>
                    {`${kidsForPlan.length}-Kids, ${registration.ticketDetails.dayType}, $${registration.ticketDetails.price}`}
                  </Text>
                </View>
                {index < registrationDetails.length - 1 && <View style={styles.separator} />}
              </View>
            );
          })}

          <View style={[styles.costContainer, styles.totalCostContainer]}>
            <Text style={styles.summaryTitle}>Total Cost:</Text>
            <Text style={styles.totalCostValue}>
              ${registrationDetails.reduce((sum, reg) => sum + reg.registrationDetails.ticketCost, 0)}
            </Text>
          </View>

          <View style={styles.costContainer}>
            <Text style={styles.summaryTitle}>Status:</Text>
            <Text style={styles.totalCostValue}>{registrationStatus}</Text>
          </View>
        </View>
      );
    }

    if (selectedPlans.length === 0) return null;

    const totalCost = selectedPlans.reduce((sum, plan) => sum + plan.price, 0);

    return (
      <View style={styles.summarySection}>
        <Text style={styles.summaryTitle}>Kid(s)</Text>

        {selectedPlans.map((plan, index) => {
          const kidsForPlan = selectedKidsMap.get(plan.ticketId) || [];

          return (
            <View key={plan.ticketId}>
              <View style={styles.planSection}>
                <Text style={styles.summaryText}>
                  {kidsForPlan.map(kid => kid.name).join(', ')}
                </Text>

                <Text style={styles.summaryPlan}>
                  {`${kidsForPlan.length}-Kids, ${plan.dayType}, $${plan.price}`}
                </Text>
              </View>
              {index < selectedPlans.length - 1 && <View style={styles.separator} />}
            </View>
          );
        })}

        <View style={[styles.costContainer, styles.totalCostContainer]}>
          <Text style={styles.summaryTitle}>Total Cost:</Text>
          <Text style={styles.totalCostValue}>${totalCost}</Text>
        </View>

        {(showPayButton || event.isRegistered) && (
          <View style={styles.costContainer}>
            <Text style={styles.summaryTitle}>Status:</Text>
            <Text style={styles.totalCostValue}>{registrationStatus}</Text>
          </View>
        )}
      </View>
    );
  };

  const handleNextImage = () => {
    if (event.pic_url && currentImageIndex < event.pic_url.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  return (
    loading ? null : (
      <View style={styles.container}>
        <TopGreenHeader Heading="Details" navigation={navigation} isNotification={false} />
        {Platform.OS === 'web' && <style>{toastifyCss}</style>}
        <ScrollView style={styles.scrollView}>
          <View style={styles.bannerContainer}>
            {event.pic_url && event.pic_url.length > 0 ? (
              <>
                <Image
                  source={{ uri: event.pic_url[currentImageIndex] }}
                  style={styles.bannerImage}
                  resizeMode="cover"
                />
                {event.pic_url.length > 1 && (
                  <>
                    <TouchableOpacity
                      style={[styles.navButton, styles.prevButton]}
                      onPress={handlePrevImage}
                      disabled={currentImageIndex === 0}
                    >
                      <Text style={[styles.navButtonText, currentImageIndex === 0 && styles.disabledButton]}>❮</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.navButton, styles.nextButton]}
                      onPress={handleNextImage}
                      disabled={currentImageIndex === event.pic_url.length - 1}
                    >
                      <Text style={[styles.navButtonText, currentImageIndex === event.pic_url.length - 1 && styles.disabledButton]}>❯</Text>
                    </TouchableOpacity>
                    <View style={styles.paginationContainer}>
                      {event.pic_url.map((_, index) => (
                        <View
                          key={index}
                          style={[
                            styles.paginationDot,
                            index === currentImageIndex && styles.paginationDotActive
                          ]}
                        />
                      ))}
                    </View>
                  </>
                )}
              </>
            ) : null}
          </View>

          <View style={styles.contentContainer}>
            <View style={styles.titleRow}>
              <Text style={styles.title}>{event.title}</Text>
              <View style={styles.statusContainer}>
                <View style={[styles.statusBadge,
                event.statusType === 'ongoing' ? styles.onGoing :
                  event.statusType === 'completed' ? styles.completed :
                    styles.upcoming]}>
                  <Text style={[styles.statusText,
                  event.statusType === 'ongoing' ? styles.onGoingText :
                    event.statusType === 'completed' ? styles.completedText :
                      styles.upcomingText]}>{event.statusType}</Text>
                </View>
              </View>
            </View>
            <Text style={styles.date}>{event.date}</Text>
            <Text style={styles.registrationStatus}>
              {event.registrationStatus.includes('Booked') ? (
                <>
                  Registration Opened <Text style={styles.bookedText}>(Booked)</Text>
                </>
              ) : (
                event.registrationStatus
              )}
            </Text>

            <View style={styles.seatsRow}>
              <Text style={styles.seatsLabel}>Seats: </Text>
              <Text style={styles.seatsRemaining}>{event.availableSeats}/{event.totalSeats} remaining</Text>
            </View>

            <View style={styles.bulletList}>
              {event.details && (
                <View style={styles.detailsContainer}>
                  <RenderHtml
                    contentWidth={300}
                    source={{ html: event.details }}
                    tagsStyles={{
                      p: {
                        marginBottom: 8,
                        fontSize: 16,
                        lineHeight: 20,
                        color: '#000000'
                      },
                      ul: {
                        marginLeft: 0,
                        marginBottom: 8,
                        paddingLeft: 20
                      },
                      li: {
                        marginBottom: 4,
                        fontSize: 16,
                        lineHeight: 20,
                        color: '#000000'
                      },
                      strong: {
                        fontWeight: 'bold',
                        color: '#000000'
                      }
                    }}
                  />
                </View>
              )}

              {renderSelectedSummary()}

              {!event.isRegistered && !registrationDetails && (
                <>
                  <TouchableOpacity onPress={() => setModalVisible(true)}>
                    <View style={styles.addKidButton}>
                      <Image source={require('../../assets/plus.png')} style={styles.plusIcon} />
                      <Text style={styles.addKidButtonText}>Add Kid(s)</Text>
                    </View>
                  </TouchableOpacity>

                  <View style={styles.bookButtonContainer}>
                    <TouchableOpacity
                      style={styles.bookButton}
                      onPress={handleBook}
                    >
                      <Text style={styles.bookButtonText}>Book</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}

              {showPayButton && (
                <View style={styles.bookButtonContainer}>
                  <TouchableOpacity
                    style={styles.bookButton}
                    onPress={() => {/* Handle payment */ }}
                  >
                    <Text style={styles.bookButtonText}>Pay</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        </ScrollView>

        <AddKidsModal
          visible={modalVisible}
          onClose={handleModalClose}
          onSave={handleModalSave}
          selectedKids={selectedKids}
          onToggleKid={toggleKidSelection}
          tickets={event?.tickets || []}
        />

      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  scrollView: {
    flex: 1,
  },
  bannerContainer: {
    width: '100%',
    height: 320,
    position: 'relative',
  },
  bannerImage: {
    width: '100%',
    height: '100%',
  },
  contentContainer: {
    padding: 20,
  },
  titleRow: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 24,
    color: '#000000',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  date: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 5,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  registrationStatus: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 5,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  bookedText: {
    color: '#EE3897',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans Bold',
  },
  seatsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  seatsLabel: {
    fontSize: 16,
    color: '#000000',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  seatsRemaining: {
    fontSize: 16,
    color: '#E94D8B',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  bulletList: {
    marginBottom: 20,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  bullet: {
    fontSize: 20,
    marginRight: 10,
    color: '#000',
    lineHeight: 24,
  },
  bulletText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000000',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans Bold',
  },
  bulletTextValue: {
    fontSize: 18,
    color: '#000000',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  addKidButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,

  },
  addKidButtonText: {
    fontSize: 18,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',

  },
  plusIcon: {
    width: 30,
    height: 40,
  },
  bookButtonContainer: {
    marginTop: 20,
    alignItems: 'center',
    marginBottom: 20,
  },
  bookButton: {
    backgroundColor: '#8DC63F',
    borderRadius: 10,
    paddingVertical: 15,
    alignItems: 'center',
    width: '20%',
    marginTop: 20,
  },
  bookButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  statusContainer: {

    alignItems: 'center',
  },
  statusText: {
    fontSize: 16,
    color: '#666666',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans Bold',
  },
  onGoing: {
    backgroundColor: '#B7410E66',
  },
  upcoming: {
    backgroundColor: '#00BFFF66',
  },
  completed: {
    backgroundColor: '#556B2F66',
  },
  onGoingText: {
    color: '#B7410E',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  upcomingText: {
    color: '#00BFFF',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  completedText: {
    color: '#556B2F',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  selectedKidsContainer: {
    marginTop: 20,
  },
  selectedKidRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  kidInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedKidImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 15,
  },
  selectedKidName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000000',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans Bold',
  },
  selectedKidDetails: {
    fontSize: 14,
    color: '#666666',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  detailsContainer: {
    width: '100%',
    marginBottom: 15,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginTop: 10,
  },
  webview: {
    flex: 1,
  },
  summarySection: {
    marginTop: 20,
  },
  summaryTitle: {
    fontSize: 16,
    color: '#000000',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  summaryText: {
    fontSize: 16,
    color: '#000000',
    marginBottom: 5,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  summaryPlan: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 5,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  costContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  totalCostContainer: {
    marginTop: 20,
  },
  totalCostValue: {
    fontSize: 16,
    color: '#000000',
    marginLeft: 5,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  planSection: {
    marginVertical: 10,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E5E5',
    marginVertical: 10,
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -20 }],
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  prevButton: {
    left: 10,
  },
  nextButton: {
    right: 10,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  disabledButton: {
    opacity: 0.5,
  },
  paginationContainer: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    marginHorizontal: 4,
  },
  paginationDotActive: {
    backgroundColor: '#ffffff',
    width: 10,
    height: 10,
    borderRadius: 5,
  },
});

export default EventDetails;
